import React, { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../../providers/AuthProvider';
import Button from '../common/Button';
import FormGroupErrorText from '../form-elements/FormGroupErrorText';
import styles from '../../styles/onboarding/OnboardingTermsOfService.module.scss';

interface OnboardingTermsOfServiceProps {
  onComplete: () => void;
}

const TermsOfServiceSchema = Yup.object().shape({
  acceptTerms: Yup.boolean().oneOf([true], 'You must accept the Terms of Service to continue'),
});

const OnboardingTermsOfService: React.FC<OnboardingTermsOfServiceProps> = ({ onComplete }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [termsContent, setTermsContent] = useState<string>('');
  const [isLoadingTerms, setIsLoadingTerms] = useState(true);
  const { acceptTerms } = useAuth();

  useEffect(() => {
    const loadTermsOfService = async () => {
      try {
        const response = await fetch('/terms-of-service.md');
        if (!response.ok) {
          throw new Error('Failed to load Terms of Service');
        }
        const content = await response.text();
        setTermsContent(content);
      } catch (error) {
        console.error('Error loading Terms of Service:', error);
        setError('Failed to load Terms of Service');
      } finally {
        setIsLoadingTerms(false);
      }
    };

    loadTermsOfService();
  }, []);

  const handleSubmit = async (values: { acceptTerms: boolean }) => {
    setIsLoading(true);
    setError(null);
    
    try {
      await acceptTerms();
      onComplete();
    } catch (error: any) {
      setError(error.message || 'Failed to accept terms');
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.termsContainer}>
      <div className={styles.termsHeader}>
        <span className={styles.termsSubtitle}>
          Please review and accept our Terms of Service to continue setting up your account.
        </span>
      </div>

      <div className={styles.termsContent}>
        <div className={styles.termsDocument}>
          {isLoadingTerms ? (
            <div className={styles.loadingContainer}>
              <p>Loading Terms of Service...</p>
            </div>
          ) : error && !termsContent ? (
            <div className={styles.errorContainer}>
              <p>Error loading Terms of Service. Please try again.</p>
            </div>
          ) : (
            <div className={styles.markdownContent}>
              <ReactMarkdown>
                {termsContent}
              </ReactMarkdown>
            </div>
          )}
        </div>

        <Formik
          initialValues={{ acceptTerms: false }}
          validationSchema={TermsOfServiceSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, setFieldValue, isValid }) => (
            <Form className={styles.termsForm}>
              <div className={styles.checkboxGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={values.acceptTerms}
                    onChange={(e) => setFieldValue('acceptTerms', e.target.checked)}
                    className={styles.checkbox}
                  />
                  <span className={styles.checkboxText}>
                    I have read and agree to the Terms of Service and our 
                    <a href="/privacy-policy.html"> Privacy Policy</a>.
                  </span>
                </label>
                {errors.acceptTerms && (
                  <FormGroupErrorText text={errors.acceptTerms} />
                )}
                {error && (
                  <FormGroupErrorText text={error} />
                )}
              </div>

              <div className={styles.buttonGroup}>
                <Button
                  type="submit"
                  btnSize="lg"
                  disabled={!isValid || isLoading}
                  isLoading={isLoading}
                  isLoadingText="Processing..."
                  text="Accept and Continue"
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default OnboardingTermsOfService;
